// Regla del hub (Ignacio, 2026-09-21): el header (logo + grid 3x2 + filtros) es del SHELL, no de
// cada tab — queda inmaculado al cambiar de tab (nunca desaparece, nunca se re-renderiza entero,
// nunca salta). Antes cada tab armaba su propio <Header> llamando a renderHeader(filtersContent)
// DENTRO de su propio árbol de render — si la tab cortaba antes con un loader (ej. Mapa de Calor,
// `if (loading) return <Loader fullPage />` antes de construir los filtros), el <Header> de esa
// tab nunca se creaba y la pantalla se quedaba sin header mientras cargaba.
//
// Mecanismo nuevo: las tabs publican sus filtros acá (por un hook que no depende de llegar a
// ningún punto particular del árbol de render) y el shell (src/pages/Ventas/index.js) arma UN
// SOLO <Header>, siempre, leyendo los filtros de la tab activa desde este estado compartido.
//
// Ver docs/modulos/ventas-hub.md — "El header es del shell" (10.250).
import { createContext, useContext, useEffect } from "react";

// Valor del contexto: la función registerFilters(tabKey, node) que expone el shell, o null fuera
// del hub (standalone) — en ese caso el hook de abajo es un no-op silencioso.
const HubHeaderContext = createContext(null);

export const HubHeaderProvider = HubHeaderContext.Provider;

/**
 * Publica los filtros de una tab en el header del shell. Hook incondicional (sin early return
 * antes de llamarlo) — se llama SIEMPRE, cargando o no, para que el header nunca dependa de que
 * la tab haya llegado a un punto particular de su render.
 * @param {string} tabKey - key estable de la tab (VENTAS_TABS), o undefined fuera del hub.
 * @param {React.ReactNode} filtersContent - los filtros actuales de la tab (o null si aún no hay).
 */
export const useHubHeaderFilters = (tabKey, filtersContent) => {
  const registerFilters = useContext(HubHeaderContext);

  // Registro: corre en cada render donde cambien los filtros — mantiene el slot del shell al día.
  useEffect(() => {
    if (!registerFilters || !tabKey) return;
    registerFilters(tabKey, filtersContent);
  }, [registerFilters, tabKey, filtersContent]);

  // Limpieza: SOLO al desmontar (dependencias estables) — nunca en cada cambio de filtersContent,
  // para no vaciar el slot un instante antes de volver a llenarlo (evita parpadeo).
  useEffect(() => {
    if (!registerFilters || !tabKey) return undefined;
    return () => registerFilters(tabKey, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerFilters, tabKey]);
};

/** true si hay un HubHeaderProvider por encima (la tab vive dentro del hub de Ventas). */
export const useIsInHub = () => useContext(HubHeaderContext) !== null;
